import React from 'react'
import { atlas, ProviderProfession } from '../api'

type AddBookingSheetProps = {
  open: boolean
  stack: ProviderProfession
  onClose: () => void
  onCreated?: (selectionId: string | null) => void
}

const normalizeSlotISO = (raw: string) => {
  const trimmed = String(raw || '').trim()
  if (!trimmed) return null
  if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)) return trimmed
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) throw new Error('Booking time is invalid')
  return parsed.toISOString()
}

export default function AddBookingSheet({
  open,
  stack,
  onClose,
  onCreated,
}: AddBookingSheetProps) {
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [phone, setPhone] = React.useState('')
  const [bookingTime, setBookingTime] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)
  const [notice, setNotice] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    setErr(null)
    setNotice(null)
  }, [open])

  const resetForm = () => {
    setFirstName('')
    setLastName('')
    setEmail('')
    setPhone('')
    setBookingTime('')
  }

  const handleClose = () => {
    if (!busy) {
      resetForm()
      onClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setErr(null)
    setNotice(null)
    const fn = firstName.trim()
    const ln = lastName.trim()
    const em = email.trim().toLowerCase()
    const ph = phone.trim()
    if (!fn || !ln || !em || !ph) {
      setErr('First name, last name, email, and phone are required.')
      return
    }
    let slotISO: string | null = null
    try {
      slotISO = normalizeSlotISO(bookingTime)
    } catch (e: any) {
      setErr(e?.message || 'Booking time is invalid')
      return
    }
    setBusy(true)
    try {
      const payload: {
        firstName: string
        lastName: string
        email: string
        phone: string
        slotISO?: string
      } = {
        firstName: fn,
        lastName: ln,
        email: em,
        phone: ph,
      }
      if (slotISO) payload.slotISO = slotISO
      const res = await atlas.createManualBooking(stack, payload)
      setNotice('Booking added.')
      window.dispatchEvent(new Event('inbox:refresh'))
      onCreated?.(res?.selectionId || null)
      window.setTimeout(() => {
        resetForm()
        onClose()
      }, 400)
    } catch (e: any) {
      setErr(e?.message || 'Failed to add booking')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  return (
    <div className="overlay show inbox-overlay" role="dialog" aria-modal="true" onClick={handleClose}>
      <div className="sheet inbox-view-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="page">
          <div className="header">
            <h1 className="section-title" style={{ margin: 0 }}>Add booking</h1>
            <button className="btn" type="button" onClick={handleClose}>Close</button>
          </div>

          <p className="hint">
            Create a confirmed booking for a client. Leave booking time blank if you need to schedule later.
          </p>

          {err && <div className="banner error" role="alert" style={{ marginBottom: 12 }}>{err}</div>}
          {notice && <div className="banner success" role="status" style={{ marginBottom: 12 }}>{notice}</div>}

          <form className="card" onSubmit={handleSubmit}>
            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="field-label">First name *</div>
                <input
                  className="input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  required
                />
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="field-label">Last name *</div>
                <input
                  className="input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  required
                />
              </div>
            </div>

            <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div className="field-label">Email *</div>
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@email.com"
                  required
                />
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="field-label">Phone *</div>
                <input
                  className="input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="04xx xxx xxx"
                  required
                />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <div className="field-label">Booking time (optional)</div>
              <input
                className="input"
                type="datetime-local"
                value={bookingTime}
                onChange={(e) => setBookingTime(e.target.value)}
              />
            </div>

            <div className="inline-gap-2" style={{ marginTop: 16 }}>
              <button className="btn btn-primary" type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Add booking'}
              </button>
              <button className="btn" type="button" onClick={handleClose} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
